// Checkout.js
import { useEffect, useRef, useState } from "react"
import TagManager from "react-gtm-module"
import { Controller, useForm } from "react-hook-form"
import { BiChevronDown, BiChevronUp } from "react-icons/bi"
import { FaSpinner } from "react-icons/fa6"
import { LuChevronLeft, LuMoveLeft } from "react-icons/lu"
import {
  MdOutlineRadioButtonChecked,
  MdOutlineRadioButtonUnchecked,
} from "react-icons/md"
import { RiInformation2Line } from "react-icons/ri"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import BlurComponentWrapper from "@fuse/sharedComponents/blurWrapper/BlurComponentWrapper"
import GenericFeedback from "@fuse/sharedComponents/GenericFeedback"
import BlurredLoadingSpinner from "@fuse/sharedComponents/loader/BlurredLoadingSpinner"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  CardNumberElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import {
  chain,
  get,
  groupBy,
  isEmpty,
  isNull,
  isUndefined,
  map,
  some,
  sumBy,
  uniq,
} from "lodash"
import moment from "moment"
import { useRecoilState, useResetRecoilState } from "recoil"
import { ROUTES } from "src/App"
import * as yup from "yup"

import { allEventCacheState, eventDetailsState } from "@fuse/recoil/events"

import { splitFullName } from "@fuse/utils/DeviceDection"

import { useStripeLoading } from "@fuse/customHooks/useStripeLoading"

import { WhichBtnState } from "@fuse/pages/eventPage/controller"
import InfoModal, {
  InfoModalHandle,
} from "@fuse/pages/eventSignUp/BuyTickets/components/InfoModal"
import CheckoutForm from "@fuse/pages/eventSignUp/BuyTickets/StripeCheckout/CheckoutForm"
import { useStripeCheckout } from "@fuse/pages/eventSignUp/BuyTickets/StripeCheckout/useStripeCheckout"
import {
  getServiceFeesConfig,
  getTicketWithServiceFees,
  groupTicketsWithPrice,
} from "@fuse/pages/eventSignUp/eventHelper"

import ApplePaySmall from "@fuse/assets/images/eventSignUp/icon-payout-card/icon-apple-pay-small.svg"
import GooglePaySmall from "@fuse/assets/images/eventSignUp/icon-payout-card/icon-google-pay-small.svg"
import MasterCardSmall from "@fuse/assets/images/eventSignUp/icon-payout-card/icon-master-card-small.svg"
import StipePaySmall from "@fuse/assets/images/eventSignUp/icon-payout-card/icon-stripe-pay-small.svg"
import VisaCardSmall from "@fuse/assets/images/eventSignUp/icon-payout-card/icon-visa-card-small.svg"
import PoweredByStripeLogo from "@fuse/assets/images/eventSignUp/img-by-stripe-logo.png"
import CalendarIcon from "@fuse/assets/images/eventSignUp/img-calendar-icon.svg"
import GPayLogo from "@fuse/assets/images/eventSignUp/img-g-pay-logo.png"
import IconMoney from "@fuse/assets/images/eventSignUp/img-icon-money.svg"
import LocationIcon from "@fuse/assets/images/eventSignUp/img-location-icon.svg"

yup.addMethod(yup.object, "validateCardError", function () {
  return this.test("validate-card-error", function (value) {
    if (value.empty) {
      return this.createError({
        path: this.path,
        message: "Value is required",
      })
    }
    if (value && !value.empty && !value.complete) {
      if (value.error && value.error.message) {
        return this.createError({
          path: this.path,
          message: value.error.message,
        })
      } else {
        return this.createError({
          path: this.path,
          message: "invalid detail",
        })
      }
    }
    return true
  })
})

const schema = yup.object().shape({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  email: yup.string().email("Invalid email").optional(),
  // required("Email is required"),
  cardNumber: yup
    .object()
    .shape({
      elementType: yup.string().nullable().default("").optional(),
      error: yup
        .object()
        .shape({
          code: yup.string().nullable().default(""),
          type: yup.string().nullable().default(""),
          message: yup.string().nullable().default(""),
        })
        .nullable()
        .default({})
        .optional(),
      empty: yup.boolean().nullable().default(false).optional(),
      complete: yup.boolean().nullable().default(false).optional(),
    })
    .nullable()
    .default({})
    .optional()
    .test("validate-card-error", function (value) {
      if (value?.empty) {
        return this.createError({
          path: this.path,
          message: "Value is required",
        })
      }
      if (value && !value.empty && !value.complete) {
        if (value.error && value.error.message) {
          return this.createError({
            path: this.path,
            message: value.error.message,
          })
        } else {
          return this.createError({
            path: this.path,
            message: "invalid detail",
          })
        }
      }
      return true
    }),
  expiry: yup
    .object()
    .shape({
      elementType: yup.string().nullable().default("").optional(),
      empty: yup.boolean().nullable().default(false).optional(),
      complete: yup.boolean().nullable().default(false).optional(),
      error: yup
        .object()
        .shape({
          message: yup.string().nullable().default(""),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .default({})
    .optional()
    .test("validate-card-error", function (value) {
      if (value?.empty) {
        return this.createError({
          path: this.path,
          message: "Value is required",
        })
      }
      if (value && !value.empty && !value.complete) {
        if (value.error && value.error.message) {
          return this.createError({
            path: this.path,
            message: value.error.message,
          })
        } else {
          return this.createError({
            path: this.path,
            message: "invalid detail",
          })
        }
      }
      return true
    }),
  cvv: yup
    .object()
    .shape({
      elementType: yup.string().nullable().default("").optional(),
      empty: yup.boolean().nullable().default(false).optional(),
      complete: yup.boolean().nullable().default(false).optional(),
      error: yup
        .object()
        .shape({
          message: yup.string().nullable().default(""),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .default({})
    .optional()
    .test("validate-card-error", function (value) {
      if (value?.empty) {
        return this.createError({
          path: this.path,
          message: "Value is required",
        })
      }
      if (value && !value.empty && !value.complete) {
        if (value.error && value.error.message) {
          return this.createError({
            path: this.path,
            message: value.error.message,
          })
        } else {
          return this.createError({
            path: this.path,
            message: "invalid detail",
          })
        }
      }
      return true
    }),
  zipCode: yup.string().required("Zip Code is required"),
})

const StripeCheckout = () => {
  const { state } = useLocation()
  const invitedId = state?.invitedId
  const {
    eventId,
    isLoading,
    onInitPayment,
    setIsLoading,
    ticketsToBuy,
    refreshEventData,
    user,
    flyerImage,
    paymentFailedToast,
    eventDetail,
    applyCoupon,
    removeCoupon,
    donations,
    selectedDonateAmount,
    currentEventDetails,
  } = useStripeCheckout(invitedId, "stripeCheckout")

  const stripe = useStripe()
  const elements = useElements()
  const { stripeError, retryStripe, isStripeLoaded } = useStripeLoading()
  const navigate = useNavigate()

  const [paymentRequest, setPaymentRequest] = useState(null)
  const [paymentMethod, setPaymentMethod] = useState("card")
  const [deviceSupportedPay, setDeviceSupportedPay] = useState(null)
  const [coupon, setCoupon] = useState("")
  const [couponDetails, setCouponDetails] = useState(null)
  const [isApplied, setIsApplied] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const reset = useResetRecoilState(eventDetailsState)

  const modalRef = useRef<InfoModalHandle>(null)
  const paymentFormRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const EventStartDate = get(eventDetail, "timing.eventDate", new Date())
  const EventStartTime = get(eventDetail, "timing.eventStartTime", new Date())
  const EventEndTime = get(eventDetail, "timing.eventEndTime", new Date())
  const eventLocation = get(eventDetail, "location.eventLocation", "")

  const serviceConfigValue = getServiceFeesConfig()
  const stripeConfigFees = get(serviceConfigValue, "stripe", {})
  const service = get(serviceConfigValue, "service", {})

  const stripeConfigPercentage = get(stripeConfigFees, "percentage", 1)
  const stripeConfigConstant = get(stripeConfigFees, "constant", 0)

  const servicePercentage = get(service, "percentage", 1)
  const serviceConstant = get(service, "constant", 0)

  const groupedTickets = groupBy(ticketsToBuy, "ticketId")

  const mapTicketDetails = groupTicketsWithPrice(groupedTickets)
  const finalPrice = sumBy(mapTicketDetails, "finalPrice")

  const [cache, setCache] = useRecoilState(allEventCacheState)

  const ticketNamesString = chain(mapTicketDetails)
    .map("ticketName") // Extract ticket names
    .uniq() // Remove duplicates
    .join(", ") // Convert to a comma-separated string
    .value()

  const discountAmount = couponDetails?.discount?.discountAmount || 0

  const totalServiceFees =
    (finalPrice - discountAmount) * (servicePercentage / 100) + serviceConstant

  const totalStripeFees =
    (finalPrice - discountAmount) * (stripeConfigPercentage / 100) +
    stripeConfigConstant

  const finalPayingPrice = sumBy(mapTicketDetails, "finalPrice")
  const finalTotalPrice =
    finalPrice - discountAmount + totalServiceFees + totalStripeFees

  const finalPriceWithDonation = finalTotalPrice + (selectedDonateAmount ?? 0)

  // Handle free tickets - check if total cost is $0
  const isFreeRegistration = finalPriceWithDonation === 0

  // const { service = {}, stripeConfigFees = {} } =
  //   getTicketWithServiceFees(finalTotalPrice)

  const formatDateWithMoment = (
    date: string | undefined | Date,
    format: string,
  ) => {
    return moment(date).format(format)
  }

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted },
  } = useForm({
    defaultValues: {
      firstName:
        get(user, "firstName", "") ||
        splitFullName(user?.displayName)?.firstName,
      lastName:
        get(user, "lastName", "") || splitFullName(user?.displayName)?.lastName,
      email: get(user, "email", ""),
    },
    resolver: yupResolver(schema),
  })

  const form = watch()

  const hasEmptyValues = some(
    form,
    (value) => isUndefined(value) || isNull(value) || isEmpty(value),
  )

  // const ticketPrice = get(ticketsToBuy, "ticketPrice", 0);
  // const payingPrice = getTicketWithServiceFees(ticketPrice);

  useEffect(() => {
    if (
      !isEmpty(errors) &&
      paymentFormRef.current &&
      scrollContainerRef.current
    ) {
      // Get the container's scroll position
      const container = scrollContainerRef.current
      const formElement = paymentFormRef.current

      // Calculate position with some offset to account for padding/margins
      // On mobile (flex-col), the payment form could be lower in the DOM
      // On desktop (flex-row-reverse), it's positioned differently
      let formPosition = formElement.offsetTop

      // Add a small offset to improve visibility (20px padding)
      formPosition = Math.max(0, formPosition - 20)

      // Scroll the container to the form position
      container.scrollTo({
        top: formPosition,
        behavior: "smooth",
      })
    }
  }, [errors, paymentFormRef])

  // Handle free registration
  const handleFreeRegistration = async () => {
    try {
      setIsLoading(true)

      const result = await onInitPayment(
        coupon,
        couponDetails?.discount?.discountAmount,
      )

      if (
        result &&
        typeof result === "object" &&
        "skipPayment" in result &&
        result.skipPayment
      ) {
        // Track analytics for free registration
        TagManager.dataLayer({
          dataLayer: {
            event: "free_ticket_registration_completed",
            event_id: eventId,
            ticket_count: ticketsToBuy.length,
            total_cost: 0,
            registration_method: "free",
          },
        })

        // Navigate to success page
        navigate(ROUTES.STRIPE_SUCCESS.replace(":eventId", eventId), {
          state: {
            registrationType: "free",
            ticketCount: ticketsToBuy.length,
          },
        })
      } else {
        paymentFailedToast("Registration failed. Please try again.")
      }
    } catch (error: any) {
      paymentFailedToast(error.message || "Registration failed")
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (data: any) => {
    // If it's a free registration, handle differently
    if (isFreeRegistration) {
      await handleFreeRegistration()
      return
    }

    if (!stripe || !elements) {
      paymentFailedToast("Payment system not ready")
      return
    }

    // Check if Stripe is available
    if (!stripe || !elements) {
      setIsLoading(false)

      // Show a more helpful error message
      paymentFailedToast(
        stripeError
          ? "Payment system unavailable. Please try refreshing the page or contact support."
          : "Payment system is still loading. Please wait a moment and try again.",
      )
      return
    }

    onInitPayment(coupon, couponDetails?.discount?.discountAmount)
      .then(async (paymentData) => {
        const paymentIntent = get(paymentData, "paymentIntent", "")

        const { error: submitError } = await elements.submit()
        if (submitError?.message) {
          // Show error to your customer
          setIsLoading(false)
          paymentFailedToast(submitError.message)
          return
        }

        const paymentError = get(paymentData, "error", "")
        if (!paymentIntent || paymentError) {
          // Show error to your customer
          setIsLoading(false)
          paymentFailedToast(paymentError || "missing paymentIntent")
          return
        }

        const cardNumberElement = elements.getElement(CardNumberElement)
        if (cardNumberElement) {
          try {
            const {
              error,
              //  paymentIntent: confirmedPaymentIntent
            } = await stripe.confirmCardPayment(paymentIntent, {
              payment_method: {
                // type: "card",
                card: cardNumberElement,
                billing_details: {
                  email: get(data, "email", ""),
                  name:
                    get(data, "firstName", "") +
                    " " +
                    get(data, "lastName", ""),
                  address: {
                    postal_code: get(data, "zipCode", ""),
                  },
                },
              },
            })

            if (error) {
              paymentFailedToast(error?.message || "")
              setIsLoading(false)
            } else {
              const eventDetails = currentEventDetails
              if (cache[eventId]) {
                setCache((prev) => ({
                  ...prev,
                  [eventId]: {
                    ...prev[eventId], // preserve other fields like eventType, hasError
                    eventDetails: {
                      ...prev[eventId]?.eventDetails, // preserve existing eventDetails
                      myJoinStatus: state?.ticketsToBuy[0]?.requireApproval
                        ? WhichBtnState.UnderReview
                        : WhichBtnState.Accepted, // update only this field
                    },
                  },
                }))
              }
              reset()
              navigate(ROUTES.STRIPE_SUCCESS.replace(":eventId", eventId), {
                state: {
                  eventDetails,
                  requiredApproval: state?.ticketsToBuy[0]?.requireApproval,
                },
              })
              // setTimeout(() => {
              //   refreshEventData()
              // }, 1200)
              setIsLoading(false)
            }
          } catch (error: any) {
            paymentFailedToast(error.message)
            setIsLoading(false)
          }
        }
      })
      .catch((error) => {
        paymentFailedToast(error.message)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if (!stripe) {
      return
    }

    const totalAmount = Math.round(finalTotalPrice * 100) // Stripe expects the value in cents

    if (!totalAmount || totalAmount < 0) {
      console.error("Invalid total amount:", totalAmount)
      return
    }
    // Initialize the Payment Request API
    const pr = stripe.paymentRequest({
      country: get(user, "country", "US") || "US",
      currency: "usd",
      total: {
        label: "Total Payment",
        amount: totalAmount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    })

    // Check if the browser supports the Payment Request API
    pr.canMakePayment().then((result) => {
      if (result) {
        if (result?.applePay) {
          setDeviceSupportedPay("apple")
        }

        // Check for Google Pay
        if (result?.googlePay) {
          setDeviceSupportedPay("google")
        }

        setPaymentRequest(pr)
      }
    })

    // Handle payment submission
    pr.on("paymentmethod", async (ev) => {
      const paymentMethodConfig = {
        payment_method: ev.paymentMethod.id,
      }

      const result = await processPayment(paymentMethodConfig)

      if (result.success) {
        ev.complete("success")
      } else {
        ev.complete("fail")
      }
    })
  }, [stripe, finalTotalPrice])

  const handleBackPress = () => {
    navigate(-1)
  }

  const Header: React.FC = () => {
    return (
      <div className="absolute top-4 left-4 md:top-12 md:left-12 flex items-center space-x-2 z-20">
        <div
          onClick={handleBackPress}
          className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10 cursor-pointer"
        >
          {/* <LuMoveLeft className="text-white text-md md:text-2xl" /> */}
          <LuChevronLeft className="text-white text-2xl mr-0.5 md:hidden" />
          <LuMoveLeft className="text-white text-md md:text-2xl hidden md:flex" />
        </div>
      </div>
    )
  }

  const renderPaymentMethodIcon = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "card":
        return (
          <div className="flex gap-2 flex-row">
            <img src={VisaCardSmall} />
            <img src={StipePaySmall} />
            <img src={MasterCardSmall} />
          </div>
        )

      case "apple":
        return (
          <div className="gap-2">
            <img src={ApplePaySmall} />
          </div>
        )

      case "google":
        return (
          <div className="gap-2">
            <img src={GooglePaySmall} />
          </div>
        )

      default:
        return null
    }
  }

  const onApplyOrRemove = async () => {
    try {
      if (isButtonLoading) return // Prevent multiple requests
      setIsButtonLoading(true)

      const selectedTicketsId = uniq(map(mapTicketDetails, "ticketId"))

      const payload = {
        eventId,
        couponCode: coupon,
        totalAmount: isApplied ? finalPrice - discountAmount : finalPrice,
        checkOutData: {
          ...(!isEmpty(selectedTicketsId) && { ticketIds: selectedTicketsId }),
        },
      }
      let res
      if (isApplied) {
        res = await removeCoupon(payload)
        if (res.success) {
          setCoupon("")
          setIsApplied(false)
          setCouponDetails(null)
          setIsOpen(false)
          showSuccessToast(
            "Discount Removed. Your order total has been updated.",
          )
        } else {
          setIsButtonLoading(false)
          showErrorToast(res.message)
        }
      } else {
        res = await applyCoupon(payload)
        if (res.success) {
          setIsApplied(true)
          setCouponDetails(res.data)
          showSuccessToast(
            `Yay! you saved $${res?.data?.discount?.discountAmount} with this coupon`,
          )
        } else {
          setIsButtonLoading(false)
          showErrorToast(res.message)
        }
      }
      setIsButtonLoading(false)
      return res
    } catch (e) {
      showErrorToast(`Something went wrong`)
      setIsButtonLoading(false)
    }
  }

  const isCardSelected = paymentMethod === "card"
  const isDevicePaySelected =
    paymentMethod === "apple" || paymentMethod === "google"

  const isHaveError = hasEmptyValues && isCardSelected

  const processPayment = async (paymentMethodConfig: any) => {
    try {
      setIsLoading(true)

      const paymentData = await onInitPayment(
        coupon,
        couponDetails?.discount?.discountAmount,
      )
      const paymentIntent = get(paymentData, "paymentIntent", "")
      const paymentError = get(paymentData, "error", "")

      if (!paymentIntent || paymentError) {
        paymentFailedToast(paymentError || "missing paymentIntent")
        return { success: false }
      }

      if (!stripe || !elements) {
        paymentFailedToast("Payment system not ready")
        return { success: false }
      }

      const { error } = await stripe.confirmCardPayment(
        paymentIntent,
        paymentMethodConfig,
      )

      if (error) {
        paymentFailedToast(error?.message || "Payment failed")
        return { success: false }
      }

      // Success handling
      showSuccessToast("Ticket purchase successful")
      navigate(ROUTES.STRIPE_SUCCESS.replace(":eventId", eventId), {
        state: {
          requiredApproval: state?.ticketsToBuy[0]?.requireApproval,
        },
      })

      setTimeout(() => {
        refreshEventData()
      }, 1200)

      return { success: true }
    } catch (error: any) {
      paymentFailedToast(error.message || "Payment failed")
      return { success: false }
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading state if Stripe hasn't loaded yet
  if (!isStripeLoaded && !stripeError) {
    return (
      <BackgroundWrapper bgImage={flyerImage}>
        <BlurWrapper>
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <FancyText className="text-white">
                Loading payment system...
              </FancyText>
            </div>
          </div>
        </BlurWrapper>
      </BackgroundWrapper>
    )
  }

  // Show error state if Stripe failed to load completely
  if (stripeError) {
    return (
      <BackgroundWrapper bgImage={flyerImage}>
        <BlurWrapper>
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center max-w-md mx-auto p-6">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <FancyText className="text-white text-xl mb-2">
                Payment System Unavailable
              </FancyText>
              <FancyText className="text-white/70 mb-4">
                We're having trouble loading the payment system. This might be
                due to a network issue.
              </FancyText>
              <button
                onClick={retryStripe}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate(-1)}
                className="ml-4 bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg"
              >
                Go Back
              </button>
            </div>
          </div>
        </BlurWrapper>
      </BackgroundWrapper>
    )
  }

  return (
    <BackgroundWrapper bgImage={flyerImage} className="min-h-screen">
      <BlurWrapper
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-screen"
      >
        <div className="fixed lg:relative inset-0 w-full h-screen overflow-hidden lg:overflow-auto flex flex-col pb-20">
          <Header />
          {isLoading && <BlurredLoadingSpinner />}
          {/* <Navbar /> */}

          <div className="relative flex flex-col items-center justify-center h-full text-center my-16 md:my-28 px-6 lg:px-28">
            <div className="flex flex-col w-full max-w-sm md:max-w-2xl lg:max-w-5xl self-center">
              <p className="text-white/30 text-left text-[14px] md:text-sm font-semibold">
                STEP 3
              </p>
              <p className="text-white text-left text-3xl mt-2">Checkout</p>
              <div className="text-sm text-white/50 text-start mt-2">
                Depending on the host's preferences, you might still need to be
                approved after check out
              </div>
            </div>

            <div
              ref={scrollContainerRef}
              className="flex w-full flex-col md:flex-row-reverse max-w-sm md:max-w-2xl lg:max-w-5xl self-center gap-4 lg:gap-8 my-4 mt-8 mb-20 max-h-[65vh] md:h-full overflow-auto"
            >
              {/* Order Summary */}
              <div className="flex flex-col w-full ">
                <div className="p-6 lg:p-12 rounded-3xl w-full max-w-sm md:max-w-xs lg:max-w-md bg-transparent md:bg-white/10 lg:overflow-y-auto h-full">
                  <div className="bg-white/10 rounded-lg overflow-hidden p-4 flex flex-col items-start justify-start">
                    <h2 className="text-white text-lg text-start">
                      {ticketNamesString}
                    </h2>
                    <div className="flex h-[1px] w-full bg-white/30 my-4"></div>
                    <div className="text-gray-400 gap-3 flex flex-col">
                      <div className="flex flex-row gap-2">
                        <img src={IconMoney} />
                        <p className="text-sm text-white/50">
                          ${parseFloat(finalTotalPrice.toString()).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex flex-row gap-2">
                        <img src={CalendarIcon} />
                        <p className="text-sm text-white/50 text-start">
                          {moment(EventStartDate).format("dddd, MMM D")} |{" "}
                          {`${formatDateWithMoment(EventStartTime, "h:mm A")} - ${formatDateWithMoment(
                            EventEndTime,
                            "h:mm A",
                          )}`}
                        </p>
                      </div>
                      <div className="flex flex-row gap-2">
                        <img src={LocationIcon} />
                        <p className="text-sm text-white/50">{eventLocation}</p>
                      </div>
                    </div>
                  </div>

                  <div className="text-white text-sm  my-8">
                    <div className="flex h-[1px] w-full bg-white/15 mb-5 "></div>

                    {mapTicketDetails.map((item, index) => {
                      const finalPrice = get(item, "finalPrice", "")
                      const label = get(item, "label", "")

                      return (
                        <div className="flex justify-between py-1">
                          <span className="text-start w-[70%]">{label}</span>
                          <span>
                            ${parseFloat(finalPrice.toString()).toFixed(2)}
                          </span>
                        </div>
                      )
                    })}
                    <div className="flex h-[1px] w-full bg-white/15 mb-4 mt-5"></div>

                    <div className="w-full ">
                      <div
                        className="flex justify-between items-center rounded-lg cursor-pointer"
                        onClick={() => setIsOpen(!isOpen)}
                      >
                        <span className="font-medium">
                          I have a discount code
                        </span>
                        <div className="flex justify-between items-center">
                          {isApplied && (
                            <BlurComponentWrapper className="rounded">
                              <span className="text-xs font-medium px-2 ">
                                -
                                {`${couponDetails?.coupon?.discountType === "PERCENTAGE" ? couponDetails?.coupon?.discountValue : couponDetails?.discount?.discountAmount}${couponDetails?.coupon?.discountType === "PERCENTAGE" ? "%" : ""} off applied`}
                              </span>
                            </BlurComponentWrapper>
                          )}
                          {isOpen ? (
                            <BiChevronUp size={20} />
                          ) : (
                            <BiChevronDown size={20} />
                          )}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3 rounded-lg  bg-white/10 flex items-center px-3 py-3">
                          <input
                            type="text"
                            placeholder="Enter code"
                            className={`w-full rounded-md bg-transparent outline-none ${coupon ? "uppercase" : ""}`}
                            onChange={(e) =>
                              setCoupon(e.target.value.toUpperCase())
                            }
                            value={coupon}
                            disabled={isApplied}
                          />
                          <button
                            onClick={onApplyOrRemove}
                            className="cursor-pointer bg-transparent p-0"
                            disabled={isButtonLoading || !coupon}
                          >
                            {isButtonLoading ? (
                              <FaSpinner className="animate-spin" />
                            ) : isApplied ? (
                              "REMOVE"
                            ) : (
                              "APPLY"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex h-[1px] w-full bg-white/15 mt-5 mb-4"></div>
                    {isApplied && (
                      <>
                        <div className="flex justify-between items-center ">
                          <div className="flex flex-col text-start">
                            <span className="text-white">Discount</span>
                          </div>
                          <span className="text-white">
                            -$
                            {parseFloat(
                              `${couponDetails?.discount?.discountAmount}`,
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex h-[1px] w-full bg-white/15 my-4"></div>{" "}
                      </>
                    )}
                    <div className="flex justify-between items-center w-full">
                      <div className="text-center w-full">
                        {donations?.length > 0 && selectedDonateAmount ? (
                          <>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col text-start">
                                <span className="text-white">
                                  {donations[0].ticketName}
                                </span>
                                <span className="text-white/35 text-xs">
                                  Donations
                                </span>
                              </div>
                              <span className="text-white">
                                $
                                {parseFloat(`${selectedDonateAmount}`).toFixed(
                                  2,
                                )}
                              </span>
                            </div>

                            <div className="flex h-[1px] w-full bg-white/15 my-4"></div>
                          </>
                        ) : null}

                        <div className="flex justify-between items-center">
                          <div className="flex flex-col text-start">
                            <span className="text-white">Service Fee</span>
                            {/* <span className="text-white/35 text-xs">{`${service?.percentage} % ${service?.constant ? "+ service?.constant" : ""} per ticket + addOns`}</span> */}
                          </div>
                          <span className="text-white">
                            ${parseFloat(`${totalServiceFees}`).toFixed(2)}
                          </span>
                        </div>

                        <div className="flex h-[1px] w-full bg-white/15 my-4"></div>

                        <div className="flex justify-between items-center">
                          <div className="flex flex-col text-start">
                            <span className="text-white">Processing Fee</span>
                            {/* <span className="text-white/35 text-xs">
                            {`${stripeConfigFees?.percentage}% + ${stripeConfigFees?.constant} per ticket + addOns`}
                          </span> */}
                          </div>
                          <span className="text-white">
                            ${parseFloat(`${totalStripeFees}`).toFixed(2)}
                          </span>
                        </div>

                        <div className="flex h-[1px] w-full bg-white/15 my-4"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between text-white text-sm mb-4 items-end">
                    <span>Total</span>
                    <span className="text-lg font-semibold">
                      {isApplied
                        ? `$${parseFloat(`${finalPriceWithDonation}`).toFixed(
                            2,
                          )}`
                        : `$${parseFloat(`${finalPriceWithDonation}`).toFixed(
                            2,
                          )}`}
                    </span>
                  </div>
                  <div className="w-full flex items-end justify-end -mt-8 -z-10">
                    <img
                      src={PoweredByStripeLogo}
                      className="w-[100px] h-[50px] object-contain"
                      style={{ filter: "grayscale(100%)" }}
                    />
                  </div>
                </div>
                {!isDevicePaySelected && (
                  <div
                    className={`items-center justify-center mt-10 w-full self-center sticky bottom-0 hidden md:flex pr-6`}
                  >
                    <button
                      type="button"
                      className="px-6 lg:px-8 w-full bg-white/15 rounded-full py-3"
                      onClick={handleSubmit(onSubmit)}
                    >
                      <p className="text-[12px] md:text-[15px] text-white">
                        Pay $
                        {parseFloat(`${finalPriceWithDonation}`).toFixed(2)}
                      </p>
                    </button>
                  </div>
                )}
              </div>
              {/* Payment Form */}
              <div
                ref={paymentFormRef}
                className="flex w-full max-w-sm md:max-w-lg"
              >
                <div className={`flex flex-col gap-4 w-full`}>
                  <div className="flex items-start justify-start">
                    <div className="w-full bg-white/5 flex p-4 rounded-xl flex-col ">
                      <button
                        onClick={() => setPaymentMethod("card")}
                        className={`w-full items-center justify-center flex flex-row bg-transparent text-white p-0`}
                      >
                        {isCardSelected ? (
                          <MdOutlineRadioButtonChecked
                            className={`text-white text-2xl`}
                          />
                        ) : (
                          <MdOutlineRadioButtonUnchecked
                            className={`text-white text-2xl`}
                          />
                        )}
                        <span className="flex-grow text-start ml-2 font-light">
                          Pay with Credit Card
                        </span>

                        {renderPaymentMethodIcon("card")}
                      </button>
                      <form
                        onSubmit={handleSubmit(onSubmit)}
                        className={`w-full flex flex-col items-start justify-start flex-grow ${
                          isCardSelected ? "flex" : "hidden"
                        }`}
                      >
                        <div className="grid grid-cols-2 gap-4 max-w-sm md:max-w-lg w-full mt-8">
                          <div className="flex w-full flex-col">
                            <FancyText className="text-white text-sm text-left">
                              First name
                            </FancyText>

                            <Controller
                              control={control}
                              name="firstName"
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <input
                                  type="text"
                                  name="firstName"
                                  value={value}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  placeholder="John"
                                  className="text-md md:text-base bg-white/10 p-4 rounded-md placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight mt-2"
                                />
                              )}
                            />
                            {errors["firstName"] && (
                              <FancyText className="text-xs md:text-base text-textError font-normal text-left">
                                {errors["firstName"]?.message}
                              </FancyText>
                            )}
                          </div>

                          <div className="flex w-full flex-col">
                            <FancyText className="text-white text-sm text-left">
                              Last name
                            </FancyText>

                            <Controller
                              control={control}
                              name="lastName"
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <input
                                  type="text"
                                  name="lastName"
                                  value={value}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  placeholder="Doe"
                                  className="text-md md:text-base bg-white/10 p-4 rounded-md placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight mt-2"
                                />
                              )}
                            />
                            {errors["lastName"] && (
                              <FancyText className="text-xs md:text-base text-textError font-normal text-left">
                                {errors["lastName"]?.message}
                              </FancyText>
                            )}
                          </div>
                        </div>

                        <div className="max-w-sm md:max-w-lg w-full mt-4">
                          <div className="flex w-full flex-col">
                            <FancyText className="text-white text-sm text-left">
                              Email account
                            </FancyText>

                            <Controller
                              control={control}
                              name="email"
                              render={({
                                field: { onChange, onBlur, value },
                              }) => (
                                <input
                                  type="text"
                                  name="email"
                                  value={value}
                                  onChange={onChange}
                                  onBlur={onBlur}
                                  placeholder="example@email.com"
                                  className="text-md md:text-base bg-white/10 p-4 rounded-md placeholder-white/30 text-white border-0 focus:outline-none font-[400] leading-tight mt-2"
                                />
                              )}
                            />
                            {errors["email"] && (
                              <FancyText className="text-xs md:text-base text-textError font-normal text-left">
                                {errors["email"]?.message}
                              </FancyText>
                            )}
                          </div>
                        </div>

                        <CheckoutForm control={control} errors={errors} />

                        <div className="w-full flex max-w-sm md:max-w-lg ">
                          <img
                            src={PoweredByStripeLogo}
                            className="w-[100px] h-[50px] object-contain"
                            style={{ filter: "grayscale(100%)" }}
                          />
                        </div>
                      </form>
                    </div>
                  </div>

                  {deviceSupportedPay && (
                    <div className="flex items-start justify-start">
                      <div
                        className="w-full bg-white/10 p-4 rounded-xl flex flex-col "
                        onClick={() => setPaymentMethod(deviceSupportedPay)}
                      >
                        {/* Pay Button */}
                        <button className="w-full flex items-center justify-center bg-transparent text-white p-0">
                          {isDevicePaySelected ? (
                            <MdOutlineRadioButtonChecked className="text-white text-2xl" />
                          ) : (
                            <MdOutlineRadioButtonUnchecked className="text-white text-2xl" />
                          )}
                          <span className="flex-grow text-start ml-2 font-light">
                            {deviceSupportedPay === "apple"
                              ? "Apple Pay"
                              : "Google Pay"}
                          </span>
                          {renderPaymentMethodIcon(deviceSupportedPay)}
                        </button>

                        {/* PaymentRequestButtonElement */}
                        <div
                          className={`mt-4 md:mt-8 ${
                            isDevicePaySelected ? "flex" : "hidden"
                          }`}
                        >
                          <PaymentRequestButtonElement
                            options={{ paymentRequest }}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <GenericFeedback fromScreen="Checkout" userId={user?.uid} />
              {/* {!isDevicePaySelected && (
                <BlurWrapper
                  overlayOpacityDefault="75"
                  overlayColorDefault="dark"
                  className="absolute bottom-0 left-0 right-0 items-center justify-center mt-10 hidden md:flex"
                >
                  <div
                    className={`w-full self-center sticky bottom-0 md:hidden`}
                  >
                    <button
                      type="button"
                      className="px-6 lg:px-8 w-full bg-white/15 rounded-full py-3"
                      onClick={handleSubmit(onSubmit)}
                    >
                      <p className="text-[12px] md:text-[15px] text-white">
                        Pay $
                        {parseFloat(`${finalPriceWithDonation}`).toFixed(2)}
                      </p>
                    </button>
                  </div>
                </BlurWrapper>
              )} */}
            </div>
          </div>
          {!isDevicePaySelected && (
            <div className="absolute md:relative bottom-0 left-0 right-0 md:hidden">
              <div className="flex items-center justify-center my-8 self-center">
                <button
                  className={`px-6 lg:px-8 min-w-80 bg-white/10 rounded-full border ${isHaveError ? "text-white/50" : "text-white"}`}
                  onClick={handleSubmit(onSubmit)}
                >
                  Pay ${parseFloat(`${finalPriceWithDonation}`).toFixed(2)}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="absolute bottom-4 left-0 right-0">
          <GenericFeedback
            fromScreen="Checkout"
            userId={user?.uid}
            showOnDesktop={true}
          />
        </div>
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default StripeCheckout
